import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Alert,
  SelectChangeEvent,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  AdminPanelSettings as AdminIcon,
  Security as SecurityIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { useRBAC } from '../../context/RBACContext';
import { usePermissions } from '../../hooks/usePermissions';
// Import original types from types and api.service
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import type { Permission } from '../../services/rbac/permission.service';
import { Role } from '../../types/index';

interface RoleManagerProps {
  organizationId?: string;
  teamId?: string;
  level?: 'system' | 'organization' | 'team'; // Use level prop
}

/**
 * Role Manager Component for creating and managing roles with permissions
 */
const RoleManager: React.FC<RoleManagerProps> = ({
  organizationId,
  teamId,
  level = 'organization' // Default level
}) => {
  const {
    roles,
    permissions: fetchedPermissionsList, // Rename to avoid conflict
    isLoading,
    error,
    loadRoles,
    loadPermissions,
    createRole,
    updateRole,
    deleteRole
  } = useRBAC();

  // NEW: Use the real permission system
  const userPermissionsHook = usePermissions();

  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // State holds permission IDs (strings)
  const [dialogRole, setDialogRole] = useState<{
    name: string;
    description: string;
    permissions: string[]; 
  }>({
    name: '',
    description: '',
    permissions: []
  });
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  // Local state for the full list of available permissions (with IDs)
  const [localPermissions, setLocalPermissions] = useState<Permission[]>([]); 
  const [resourceFilter, setResourceFilter] = useState<string>('all');
  // Permission-based RBAC state
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [assignableRoles, setAssignableRoles] = useState<Role[]>([]);
  const [permissionCategories, setPermissionCategories] = useState<{id: string, name: string, description: string}[]>([]);
  const [permissionValidationErrors, setPermissionValidationErrors] = useState<string[]>([]);
  const [isValidatingPermissions, setIsValidatingPermissions] = useState(false);

  // Load roles and permissions when component mounts
  useEffect(() => {
    const loadData = async () => {
      try {
        // Use level for loading roles
        await loadRoles({ organizationId, teamId, level }); 
        
        console.log('🔒 RoleManager: Roles loaded:', {
          rolesCount: roles.length,
          sampleRole: roles[0],
          sampleRolePermissions: roles[0]?.permissions
        });
        
        const fetchedPermissions = await loadPermissions();
        setLocalPermissions(fetchedPermissions || []);
        
        console.log('🔒 RoleManager: Permissions loaded:', {
          permissionsCount: fetchedPermissions?.length || 0,
          samplePermission: fetchedPermissions?.[0]
        });
        
        // Load user's current permissions to check what they can assign
        await loadUserPermissions();
        
        // Load permission categories for better UI organization
        await loadPermissionCategories();
      } catch (error) {
        console.error('Failed to load data:', error);
      }
    };

    loadData();
  }, [loadRoles, loadPermissions, organizationId, teamId, level]);

  // Load user's current permissions
  const loadUserPermissions = async () => {
    try {
      // NEW: Use real permissions from the permission framework
      const currentUserPermissions = userPermissionsHook.getAllPermissions();
      setUserPermissions(currentUserPermissions);
      console.log('🔒 RoleManager: Loaded user permissions:', {
        totalPermissions: currentUserPermissions.length,
        permissions: currentUserPermissions.sort(),
        hasSystemAdmin: currentUserPermissions.includes('system:admin')
      });
    } catch (error) {
      console.error('Failed to load user permissions:', error);
      setUserPermissions([]);
    }
  };

  // Load permission categories for UI organization
  const loadPermissionCategories = async () => {
    try {
      // This would need to be implemented in the API service
      // For now, we'll create mock categories based on resources
      const categories = [
        { id: 'system', name: 'System', description: 'System-level permissions' },
        { id: 'organization', name: 'Organization Management', description: 'Organization administration' },
        { id: 'team', name: 'Team Management', description: 'Team administration' },
        { id: 'member', name: 'Member Management', description: 'User and member management' },
        { id: 'role', name: 'Role Management', description: 'Role and permission management' },
        { id: 'content', name: 'Content Management', description: 'Content creation and publishing' }
      ];
      setPermissionCategories(categories);
    } catch (error) {
      console.error('Failed to load permission categories:', error);
      setPermissionCategories([]);
    }
  };

  // Validate if user can assign the selected permissions
  const validatePermissions = async (permissions: string[]) => {
    setIsValidatingPermissions(true);
    setPermissionValidationErrors([]);
    
    try {
      // Check if user has all the permissions they're trying to assign
      const errors: string[] = [];
      const hasSystemAdmin = userPermissions.includes('system:admin');
      
      if (!hasSystemAdmin) {
        for (const permission of permissions) {
          if (!userPermissions.includes(permission)) {
            errors.push(`You don't have permission to assign: ${permission}`);
          }
        }
      }
      
      setPermissionValidationErrors(errors);
      return errors.length === 0;
    } catch (error) {
      console.error('Failed to validate permissions:', error);
      setPermissionValidationErrors(['Failed to validate permissions']);
      return false;
    } finally {
      setIsValidatingPermissions(false);
    }
  }; 

  // Get unique resource types for filtering
  const resourceTypes = React.useMemo(() => {
    const permissions = Array.isArray(localPermissions) ? localPermissions : [];
    const types = permissions.map(p => p.resource);
    return ['all', ...Array.from(new Set(types))];
  }, [localPermissions]);

  // Filter permissions by resource type
  const filteredPermissions = React.useMemo(() => {
    const permissions = Array.isArray(localPermissions) ? localPermissions : [];
    return resourceFilter === 'all'
      ? permissions
      : permissions.filter(p => p.resource === resourceFilter);
  }, [localPermissions, resourceFilter]);

  // Group permissions by resource
  const groupedPermissions = React.useMemo(() => {
    const groups: Record<string, Permission[]> = {};
    
    filteredPermissions.forEach(permission => {
      if (!groups[permission.resource]) {
        groups[permission.resource] = [];
      }
      groups[permission.resource].push(permission);
    });
    
    return groups;
  }, [filteredPermissions]);

  // Open dialog for creating a new role
  const handleCreateRole = () => {
    setIsCreating(true);
    setDialogRole({
      name: '',
      description: '',
      permissions: [] // Initialize with empty string array
    });
    setDialogOpen(true);
  };

  // Open dialog for editing an existing role
  const handleEditRole = async (role: Role) => {
    setIsCreating(false);
    
    console.log('🔒 RoleManager: Raw role data for editing:', {
      roleName: role.name,
      roleId: role.id || role._id,
      roleObject: role,
      rolePermissionsRaw: role.permissions,
      rolePermissionsType: Array.isArray(role.permissions) ? 'array' : typeof role.permissions,
      rolePermissionsLength: role.permissions?.length || 0,
      permissionCount: role.permissions?.length || 0,
      allRoleKeys: Object.keys(role),
      hasPermissions: 'permissions' in role,
      hasPermissionIds: 'permissionIds' in role,
      hasPermissionList: 'permissionList' in role
    });

    let roleWithPermissions = role;
    
    // If role doesn't have detailed permissions, we need to reload roles with permissions included
    if (!role.permissions || role.permissions.length === 0) {
      console.log('🔒 RoleManager: Role has no permissions, reloading roles with permissions...');
      try {
        // Reload roles but with permissions included
        const rolesWithPermissions = await loadRoles({ 
          organizationId, 
          teamId, 
          level,
          includePermissions: true // Request permissions to be included
        });
        
        // Find the matching role in the newly loaded data
        const matchingRole = roles.find(r => (r.id || r._id) === (role.id || role._id));
        if (matchingRole && matchingRole.permissions && matchingRole.permissions.length > 0) {
          console.log('🔒 RoleManager: Found role with permissions after reload:', {
            roleId: matchingRole.id || matchingRole._id,
            roleName: matchingRole.name,
            permissionsLength: matchingRole.permissions.length,
            samplePermissions: matchingRole.permissions.slice(0, 3)
          });
          roleWithPermissions = matchingRole as any;
        } else {
          console.warn('🔒 RoleManager: Role still has no permissions after reload, proceeding with empty permissions');
        }
      } catch (error) {
        console.error('🔒 RoleManager: Failed to reload roles with permissions:', error);
        // Continue with original role data if fetch fails
      }
    }
    
    // Extract permission IDs from permission objects or use as-is if they're already strings
    const permissionIds = (roleWithPermissions.permissions || []).map((permission: any) => {
      if (typeof permission === 'string') {
        return permission;
      } else if (typeof permission === 'object') {
        // Try different possible ID fields
        return permission.id || permission._id || permission.permissionId || `${permission.resource}:${permission.action}`;
      }
      return permission;
    }).filter(id => id); // Remove any null/undefined values
    
    console.log('🔒 RoleManager: Processed permissions for editing:', {
      roleName: roleWithPermissions.name,
      extractedPermissionIds: permissionIds,
      permissionCount: permissionIds.length,
      samplePermissions: permissionIds.slice(0, 5),
      originalPermissionCount: roleWithPermissions.permissions?.length || 0
    });
    
    setDialogRole({
      name: roleWithPermissions.name,
      description: roleWithPermissions.description || '',
      permissions: permissionIds // Pre-check existing permissions
    });
    setSelectedRole(roleWithPermissions as any);
    
    // Add a debug comparison after state is set
    setTimeout(() => {
      console.log('🔒 RoleManager: Permission Matching Debug:', {
        rolePermissions: permissionIds,
        availablePermissions: localPermissions.map(p => ({
          id: p.id || `${p.resource}:${p.action}`,
          resource: p.resource,
          action: p.action,
          original: p
        })),
        matches: permissionIds.map(rolePermId => ({
          rolePermission: rolePermId,
          matchFound: localPermissions.some(p => 
            (p.id || `${p.resource}:${p.action}`) === rolePermId
          ),
          availableMatches: localPermissions.filter(p => 
            (p.id || `${p.resource}:${p.action}`) === rolePermId
          )
        }))
      });
    }, 100); // Small delay to ensure state is updated
    
    setDialogOpen(true);
  };

  // Handle form submission in dialog
  const handleDialogSubmit = async () => {
    try {
      // First validate that user can assign all the selected permissions
      const isValid = await validatePermissions(dialogRole.permissions);
      if (!isValid) {
        return; // Show validation errors, don't proceed
      }

      // Determine the level based on context or props
      const roleLevel = teamId ? 'team' : organizationId ? 'organization' : 'system';

      if (isCreating) {
        await createRole({
          name: dialogRole.name,
          description: dialogRole.description,
          permissions: dialogRole.permissions, // Pass string[]
          organizationId,
          teamId,
          level: roleLevel // Pass level
        });
      } else if (selectedRole) {
        await updateRole(selectedRole._id, {
          name: dialogRole.name,
          description: dialogRole.description,
          permissions: dialogRole.permissions // Pass string[]
        });
      }
      
      setDialogOpen(false);
      setPermissionValidationErrors([]);
    } catch (error) {
      console.error('Failed to save role:', error);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (roleId: string) => {
    setDeleteTargetId(roleId);
    setConfirmDeleteOpen(true);
  };

  // Confirm role deletion
  const handleConfirmDelete = async () => {
    if (deleteTargetId) {
      try {
        await deleteRole(deleteTargetId);
        setConfirmDeleteOpen(false);
        setDeleteTargetId(null);
      } catch (error) {
        console.error('Failed to delete role:', error);
      }
    }
  };

  // Toggle permission selection in dialog (works with string IDs)
  const handlePermissionToggle = React.useCallback((permissionId: string) => { 
    setDialogRole(prev => {
      const currentPermissions = [...prev.permissions];
      
      if (currentPermissions.includes(permissionId)) {
        return {
          ...prev,
          permissions: currentPermissions.filter(id => id !== permissionId)
        };
      } else {
        return {
          ...prev,
          permissions: [...currentPermissions, permissionId]
        };
      }
    });
  }, []);

  // Toggle all permissions for a resource (works with string IDs)
  const handleToggleResourcePermissions = (resource: string, isChecked: boolean) => {
    const resourcePermissionIds = groupedPermissions[resource]?.map(p => p.id || `${p.resource}:${p.action}`) || [];
    
    setDialogRole(prev => {
      let newPermissions = [...prev.permissions];
      
      if (isChecked) {
        // Add only permission IDs that the user can assign
        resourcePermissionIds.forEach(id => {
          const canAssign = userPermissions.includes('system:admin') || userPermissions.includes(id);
          if (canAssign && !newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
      } else {
        // Remove all permission IDs from this resource
        newPermissions = newPermissions.filter(id => !resourcePermissionIds.includes(id));
      }
      
      return {
        ...prev,
        permissions: newPermissions
      };
    });
  };

  // Check if a specific permission (by ID) is selected in the dialog state
  const isPermissionSelected = (permission: Permission): boolean => {
    const permissionId = permission.id || `${permission.resource}:${permission.action}`;
    return dialogRole.permissions.includes(permissionId);
  };

  // Check if all permissions for a resource are selected (works with string IDs)
  const areAllResourcePermissionsSelected = (resource: string): boolean => {
    const resourcePermissionIds = groupedPermissions[resource]?.map(p => p.id || `${p.resource}:${p.action}`) || [];
    if (resourcePermissionIds.length === 0) return false; 

    return resourcePermissionIds.every(id => dialogRole.permissions.includes(id));
  };

  // Change resource filter
  const handleResourceFilterChange = (event: SelectChangeEvent) => {
    setResourceFilter(event.target.value);
  };

  // Get icon for role level
  const getRoleLevelIcon = (roleLevel: string | undefined) => { // Use level with undefined check
    switch (roleLevel) {
      case 'system':
        return <AdminIcon color="primary" />;
      case 'organization':
        return <GroupIcon color="secondary" />;
      case 'team':
        return <PersonIcon color="success" />;
      default:
        return <SecurityIcon />;
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Role Management</Typography>
        {/* HIDDEN: Custom role creation not yet ready for production use.
            This button will be restored when custom role functionality is fully tested and approved.
            To re-enable: uncomment the Button component below */}
        {/* <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateRole}
        >
          Create Role
        </Button> */}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Role Name</TableCell>
                <TableCell>Level</TableCell>
                <TableCell>Description</TableCell>
                {/* HIDDEN: Permissions and Actions columns removed to simplify role display
                    These columns can be restored when custom role editing is re-enabled */}
                {/* <TableCell>Permissions</TableCell> */}
                {/* <TableCell>Actions</TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary" py={2}>
                      No roles found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                roles.map(role => (
                  <TableRow key={role._id}>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        {getRoleLevelIcon(role.level || 'organization')}
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {role.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(role.level || 'organization').charAt(0).toUpperCase() + (role.level || 'organization').slice(1)}
                        size="small"
                        color={
                          (role.level || 'organization') === 'system'
                            ? 'primary'
                            : (role.level || 'organization') === 'organization'
                              ? 'secondary'
                              : 'success'
                        }
                      />
                    </TableCell>
                    <TableCell>{role.description || '-'}</TableCell>
                    {/* HIDDEN: Permissions column - removed to simplify display */}
                    {/* <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {(role.permissions && role.permissions.length > 0) ? (
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="body2" color="textSecondary">
                              {role.permissions?.length || 0} permissions
                            </Typography>
                            {userPermissions.includes('system:admin') ||
                             (role.permissions && role.permissions.every((perm: any) =>
                               userPermissions.includes(typeof perm === 'object' ? perm.id : perm)
                             )) ? (
                              <Tooltip title="You can assign this role">
                                <Chip
                                  label="Can Assign"
                                  size="small"
                                  color="success"
                                  variant="outlined"
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip title="You don't have all permissions required for this role">
                                <Chip
                                  label="Limited Access"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No permissions assigned
                          </Typography>
                        )}
                      </Box>
                    </TableCell> */}
                    {/* HIDDEN: Actions column - removed since custom roles are not yet enabled */}
                    {/* <TableCell>
                      <Box display="flex" gap={1}>
                        {role.level !== 'system' && (userPermissions.includes('system:admin') ||
                          role.permissions.every((perm: any) =>
                            userPermissions.includes(typeof perm === 'object' ? perm.id : perm)
                          )) && (
                          <Tooltip title="Edit role">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRole(role as any)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {role.level !== 'system' && (userPermissions.includes('system:admin') ||
                          userPermissions.includes('role:delete')) && (
                          <Tooltip title="Delete role">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(role._id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                        {role.level === 'system' && (
                          <Tooltip title="System roles cannot be edited or deleted">
                            <IconButton
                              size="small"
                              disabled
                            >
                              <InfoIcon color="disabled" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell> */}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Role Edit/Create Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {isCreating ? 'Create New Role' : 'Edit Role'}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Permission-based assignment
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth sx={{ mb: 3 }}>
            <TextField
              label="Role Name"
              value={dialogRole.name}
              onChange={(e) => setDialogRole({ ...dialogRole, name: e.target.value })}
              fullWidth
              required
              variant="outlined"
              margin="normal"
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <TextField
              label="Description"
              value={dialogRole.description}
              onChange={(e) => setDialogRole({ ...dialogRole, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
              variant="outlined"
              margin="normal"
            />
          </FormControl>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1">Permissions</Typography>
            <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
              <Select
                value={resourceFilter}
                onChange={handleResourceFilterChange}
                displayEmpty
              >
                {resourceTypes.map((resource) => (
                  <MenuItem key={resource} value={resource}>
                    {resource === 'all'
                      ? 'All Resources'
                      : resource.charAt(0).toUpperCase() + resource.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Permission Validation Errors */}
          {permissionValidationErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Permission Validation Errors:</strong>
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {permissionValidationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </Alert>
          )}

          {/* Permission Selection Help */}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Box display="flex" alignItems="center">
              <InfoIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                You can only assign permissions that you yourself have. 
                {userPermissions.includes('system:admin') 
                  ? ' As a system administrator, you can assign any permission.' 
                  : ' Select permissions carefully based on your current access level.'}
              </Typography>
            </Box>
          </Alert>

          {/* Permission Categories */}
          {Object.entries(groupedPermissions).map(([resource, permissions]) => {
            // Calculate assignable permissions for this resource
            const assignablePermissions = permissions.filter(p => {
              const permissionId = p.id || `${p.resource}:${p.action}`;
              return userPermissions.includes('system:admin') || userPermissions.includes(permissionId);
            });
            const selectedCount = permissions.filter(p => 
              dialogRole.permissions.includes(p.id || `${p.resource}:${p.action}`)
            ).length;
            
            return (
              <Accordion key={resource} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                    <Typography variant="subtitle2" color="primary">
                      {resource.charAt(0).toUpperCase() + resource.slice(1)} Permissions
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip 
                        label={`${selectedCount}/${permissions.length} selected`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                      {assignablePermissions.length < permissions.length && (
                        <Chip 
                          label={`${assignablePermissions.length} assignable`} 
                          size="small" 
                          color="warning" 
                          variant="outlined"
                        />
                      )}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={areAllResourcePermissionsSelected(resource)}
                            onChange={(e) => handleToggleResourcePermissions(resource, e.target.checked)}
                            indeterminate={
                              permissions.some(p => dialogRole.permissions.includes(p.id || `${p.resource}:${p.action}`)) &&
                              !areAllResourcePermissionsSelected(resource)
                            }
                            disabled={assignablePermissions.length === 0}
                          />
                        }
                        label="Select All"
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
              <AccordionDetails>
                <FormGroup>
                  <Box display="flex" flexWrap="wrap">
                    {permissions.map((permission, index) => {
                      // Use 'id' field from API response
                      const permissionId = permission.id || `${permission.resource}:${permission.action}`;
                      const canAssign = userPermissions.includes('system:admin') || 
                                       userPermissions.includes(permissionId);
                      const isChecked = dialogRole.permissions.includes(permissionId);
                      
                      // Debug logging for the first few permissions only
                      if (index < 3 && !isCreating && selectedRole) {
                        console.log(`🔒 Permission Check [${resource}:${permission.action}]:`, {
                          permissionObject: permission,
                          computedPermissionId: permissionId,
                          dialogRolePermissions: dialogRole.permissions,
                          isChecked: isChecked,
                          includes: dialogRole.permissions.includes(permissionId),
                          canAssign: canAssign,
                          exactMatch: dialogRole.permissions.find(p => p === permissionId),
                          partialMatches: dialogRole.permissions.filter(p => p.includes(permission.action) || p.includes(permission.resource))
                        });
                      }
                      
                      return (
                        <FormControlLabel
                          key={permissionId}
                          control={
                            <Checkbox
                              checked={isChecked}
                              onChange={() => handlePermissionToggle(permissionId)}
                              disabled={!canAssign}
                              sx={{
                                // Gray out disabled checkboxes
                                '&.Mui-disabled': {
                                  color: 'text.disabled',
                                  opacity: 0.5,
                                },
                                // Keep checked disabled checkboxes visible but grayed
                                '&.Mui-disabled.Mui-checked': {
                                  color: 'text.disabled',
                                  opacity: 0.6,
                                }
                              }}
                            />
                          }
                          label={
                            <Box sx={{ opacity: canAssign ? 1 : 0.5 }}>
                              <Box display="flex" alignItems="center">
                                <Typography 
                                  variant="body2" 
                                  color={canAssign ? 'textPrimary' : 'textSecondary'}
                                  sx={{ fontWeight: isChecked ? 'medium' : 'normal' }}
                                >
                                  {permission.action.charAt(0).toUpperCase() + permission.action.slice(1)}
                                </Typography>
                                {!canAssign && (
                                  <Tooltip title="You don't have this permission to assign">
                                    <WarningIcon sx={{ ml: 0.5, fontSize: 16, color: 'warning.main' }} />
                                  </Tooltip>
                                )}
                                {isChecked && canAssign && (
                                  <Typography variant="caption" color="primary.main" sx={{ ml: 1 }}>
                                    ✓
                                  </Typography>
                                )}
                              </Box>
                              <Typography 
                                variant="caption" 
                                color={canAssign ? 'textSecondary' : 'text.disabled'}
                              >
                                {permission.description || `${permission.action} ${permission.resource}`}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            width: '50%', 
                            mb: 1,
                            // Gray out the entire label for disabled permissions
                            opacity: canAssign ? 1 : 0.6
                          }}
                        />
                      );
                    })}
                  </Box>
                </FormGroup>
              </AccordionDetails>
            </Accordion>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDialogSubmit} 
            variant="contained" 
            disabled={isValidatingPermissions || !dialogRole.name.trim()}
            startIcon={isValidatingPermissions ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            {isValidatingPermissions ? 'Validating...' : (isCreating ? 'Create Role' : 'Update Role')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this role? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManager;
